/**
 * use-notification-settings.ts — "Thông báo tin nhắn".
 *
 *   Tổ chức (Cài đặt → Nhắn tin & tiếp nhận → Thông báo tin nhắn):
 *     GET/PUT /notification-settings
 *   Cá nhân (Cài đặt cá nhân → Thông báo):
 *     GET/PUT /notification-settings/me
 *
 * Tuỳ chọn cá nhân được chép vào localStorage để NotificationCenter có ngay khi
 * mở app (trước khi API trả về) — máy chủ vẫn là nguồn thật, đồng bộ giữa máy.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api-client'
import { mergePrefs, type UserNotifyPrefs } from '@/lib/notify/notify-decision'

export interface MessageNotifySettings {
  aiList: boolean
  unassigned: boolean
  assigned: 'assignee' | 'all'
  reminder: { enabled: boolean; minutes: number }
}

export const notifyKeys = {
  org: ['notification-settings'] as const,
  me: ['notification-settings', 'me'] as const,
}

const LS_KEY = 'ecdp_notify_prefs'

function readCachedPrefs(): UserNotifyPrefs | undefined {
  try {
    const raw = localStorage.getItem(LS_KEY)
    return raw ? mergePrefs(JSON.parse(raw)) : undefined
  } catch {
    return undefined
  }
}

function writeCachedPrefs(p: UserNotifyPrefs) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(p)) } catch { /* chế độ riêng tư / hết chỗ */ }
}

export function useMessageNotifySettings(enabled = true) {
  return useQuery({
    queryKey: notifyKeys.org,
    enabled,
    queryFn: async () => (await api.get<{ settings: MessageNotifySettings }>('/notification-settings')).data.settings,
  })
}

export function useSaveMessageNotifySettings() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (patch: Partial<MessageNotifySettings>) =>
      (await api.put<{ settings: MessageNotifySettings }>('/notification-settings', patch)).data.settings,
    onSuccess: (s) => qc.setQueryData(notifyKeys.org, s),
  })
}

export function useMyNotifyPrefs(enabled = true) {
  return useQuery({
    queryKey: notifyKeys.me,
    enabled,
    initialData: readCachedPrefs,
    // Bản trong localStorage chỉ để khởi động nhanh — luôn hỏi lại máy chủ.
    initialDataUpdatedAt: 0,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const prefs = mergePrefs((await api.get<{ prefs: unknown }>('/notification-settings/me')).data.prefs)
      writeCachedPrefs(prefs)
      return prefs
    },
  })
}

export function useSaveMyNotifyPrefs() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (patch: Partial<UserNotifyPrefs>) =>
      mergePrefs((await api.put<{ prefs: unknown }>('/notification-settings/me', patch)).data.prefs),
    // Cập nhật lạc quan: công tắc phản hồi ngay, lỗi thì trả lại.
    onMutate: async (patch) => {
      await qc.cancelQueries({ queryKey: notifyKeys.me })
      const prev = qc.getQueryData<UserNotifyPrefs>(notifyKeys.me)
      if (prev) qc.setQueryData(notifyKeys.me, mergePrefs({ ...prev, ...patch }))
      return { prev }
    },
    onError: (_e, _p, ctx) => {
      if (ctx?.prev) qc.setQueryData(notifyKeys.me, ctx.prev)
    },
    onSuccess: (prefs) => {
      writeCachedPrefs(prefs)
      qc.setQueryData(notifyKeys.me, prefs)
    },
  })
}
